import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { getter } from "@progress/kendo-react-common";
import { process } from "@progress/kendo-data-query";
import { setGroupIds, setExpandedState, getGroupIds } from "@progress/kendo-react-data-tools";
import { LoginContext } from "../../../../services/api/authentication-api";
import { initialDataState } from "./CommonUtil";
import PlaceholderSimpleEmptyScreen from "../../../../components/PlaceholderSimpleEmptyScreen";
import { OrganizationGridButtons } from "./OrganizationGridButtons";
import { OrganizationGridContent } from "./OrganizationGridContent";
import "./organization.scss";

const processWithGroups = (data, dataState) => {
    const newDataState = process(data, dataState);
    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
};

export const OrganizationGrid = ({
    data,
    type,
    fetchEndpoint,
    privilegeKey,
    createPath,
    columns,
    exportFileName,
    placeholderTexts,
    placeholderImage,
}) => {
    const idGetter = getter("Id");
    const [currentSelectedState, setCurrentSelectedState] = useState({});
    const [dataState, setDataState] = useState(initialDataState);
    const [dataResult, setDataResult] = useState(process([], dataState));
    const [collapsedState, setCollapsedState] = useState([]);
    const _export = useRef(null);
    const user = useContext(LoginContext);
    const DATA_ITEM_KEY = "Id";
    const SELECTED_FIELD = "selected";

    const canCreate = user?.currentUser?.privileges?.[privilegeKey]?.Add ?? false;
    const containRecords = data?.data?.length > 0;
    const canDownload = containRecords;

    const selectedIds = Object.keys(currentSelectedState).filter((id) => currentSelectedState[id]);
    const hasSelectedItems = selectedIds.length > 0;
    const canArchive = user?.currentUser?.privileges?.[privilegeKey]?.Delete && hasSelectedItems;

    const onDataStateChange = (e) => {
        if (e.dataState.group) {
            e.dataState.group = e.dataState.group.filter(
                (obj, index) => e.dataState.group.findIndex((item) => item.field === obj.field) === index
            );
        }
        setDataState(e.dataState);
        setDataResult(processWithGroups(data.data, e.dataState));
    };

    const onExpandChange = useCallback(
        (event) => {
            const item = event.dataItem;
            if (item.groupId) {
                const collapsedIds = !event.value
                ? [...collapsedState, item.groupId]
                : collapsedState.filter((groupId) => groupId !== item.groupId);
                setCollapsedState(collapsedIds);
            }
        },
        [collapsedState]
    );

    const onGroupsToggle = useCallback(() => {
        const dataStateGroups = processWithGroups(data.data, {
            group: dataState.group,
        });
        setCollapsedState(collapsedState.length ? [] : getGroupIds({ data: dataStateGroups.data }));
    }, [collapsedState, data.data, dataState]);

    const expandedResult = setExpandedState({
        data: dataResult.data,
        collapsedIds: collapsedState,
    });

    const setSelectedValue = (data) => {
        let newData = data.map((item) => {
        if (item.items) {
            return {
                ...item,
                items: setSelectedValue(item.items)
            };
        } else {
            return {
                ...item,
                [SELECTED_FIELD]: currentSelectedState[idGetter(item)]
            };
        }
        });
        return newData;
    };

    const newData = setExpandedState({
        data: setSelectedValue(dataResult.data),
        collapsedIds: [],
    });

    const onHeaderSelectionChange = useCallback(
        (event) => {
            const checkboxElement = event.syntheticEvent.target;
            const checked = checkboxElement.checked;
            const newSelectedState = {};
            data.data.forEach((item) => {
                newSelectedState[idGetter(item)] = checked;
            });
            setCurrentSelectedState(newSelectedState);
            const newData = data.data.map((item) => ({
                ...item,
                [SELECTED_FIELD]: checked,
            }));
            const newDataResult = processWithGroups(newData, dataState);
            setDataResult(newDataResult);
        },
        [data, dataState]
    );

    const onSelectionChange = (event) => {
        const selectedProductId = event.dataItem.Id;
        const newSelectedState = {
            ...currentSelectedState,
            [selectedProductId]: !currentSelectedState[selectedProductId],
        };
        setCurrentSelectedState(newSelectedState);
        const newData = data.data.map((item) => ({
            ...item,
            [SELECTED_FIELD]: newSelectedState[idGetter(item)],
        }));
        const newDataResult = processWithGroups(newData, dataState);
        setDataResult(newDataResult);
    };

    const getNumberOfItems = (data) => {
        let count = 0;
        data.forEach((item) => {
            if (item.items) count += getNumberOfItems(item.items);
            else count++;
        });
        return count;
    };

    const getNumberOfSelectedItems = (data) => {
        let count = 0;
        data.forEach((item) => {
            if (item.items) count += getNumberOfSelectedItems(item.items);
            else count += item.selected ? 1 : 0;
        });
        return count;
    };

    const checkHeaderSelectionValue = () => {
        let selectedItems = getNumberOfSelectedItems(newData);
        return newData.length > 0 && selectedItems === getNumberOfItems(newData);
    };

    useEffect(() => {
        setDataResult(process(data.data, dataState));
    }, [data, dataState]);

    const excelExport = async () => {
        if (_export.current !== null) {
            let exportData = { data: data?.data, total: data?.data.length };
            if (dataState.filter) {
                let dState = { ...dataState };
                dState.take = dataResult.total;
                exportData = process(data?.data, dState);
            }
            await _export.current.save(exportData);
        }
    };

    return (
        <div className="organization-tab-container" data-testid="organization-grid">
            <OrganizationGridButtons
                createPath={createPath}
                canCreate={canCreate}
                canDownload={canDownload}
                canArchive={canArchive}
                excelExport={excelExport}
                selectedIds={selectedIds}
                fetchEndpoint={fetchEndpoint}
                type={type}
            />
            {   !containRecords && (
                <PlaceholderSimpleEmptyScreen
                    TextFirstLine={placeholderTexts.firstLine}
                    TextSecondLine={placeholderTexts.secondLine}
                    Image={placeholderImage}
                />
            )}
            {   containRecords && (
                <OrganizationGridContent
                    expandedResult={expandedResult}
                    dataResult={dataResult}
                    dataState={dataState}
                    onDataStateChange={onDataStateChange}
                    onSelectionChange={onSelectionChange}
                    onHeaderSelectionChange={onHeaderSelectionChange}
                    handleRowClick={onSelectionChange}
                    DATA_ITEM_KEY={DATA_ITEM_KEY}
                    SELECTED_FIELD={SELECTED_FIELD}
                    checkHeaderSelectionValue={checkHeaderSelectionValue}
                    columns={columns}
                    _export={_export}
                    exportFileName={exportFileName}
                />
            )}
        </div>
    );
};

OrganizationGrid.propTypes = {
    data: PropTypes.object.isRequired,
    type: PropTypes.string.isRequired,
    fetchEndpoint: PropTypes.func.isRequired,
    privilegeKey: PropTypes.string.isRequired,
    createPath: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string,
            title: PropTypes.string,
            cells: PropTypes.object,
        })
    ).isRequired,
    exportFileName: PropTypes.string.isRequired,
    placeholderTexts: PropTypes.shape({
        firstLine: PropTypes.string.isRequired,
        secondLine: PropTypes.string.isRequired,
    }).isRequired,
    placeholderImage: PropTypes.string.isRequired,
};
